import React, { useState, useEffect } from 'react';
import { GRADE_LEVELS } from '../utils/gradeLevels';
import { createApiUrl } from '../utils/apiConfig';
import { isSubscriptionLessonSelectable } from '../utils/assetDisplayName';
import axios from 'axios';
import DragDropFileUpload from './DragDropFileUpload';

// Fixed Markdown renderer that actually works
const SimpleMarkdown = ({ children }) => {
  if (!children) return null;
  
  const text = children.toString();
  
  // Split into lines and process each line individually
  const lines = text.split('\n');
  const processedLines = [];
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (!line) {
      // Empty line - close any open list
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push('<br />');
      continue;
    }
    
    // Check for headers (must be at start of line)
    if (line.startsWith('# ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      const headerText = line.substring(2);
      processedLines.push(`<h3 style="color: #333; margin: 20px 0 15px 0; border-bottom: 2px solid #007bff; padding-bottom: 8px; font-size: 24px;">${headerText}</h3>`);
    } else if (line.startsWith('## ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      const headerText = line.substring(3);
      processedLines.push(`<h4 style="color: #555; margin: 18px 0 12px 0; border-left: 4px solid #007bff; padding-left: 15px; font-size: 20px;">${headerText}</h4>`);
    } else if (line.startsWith('### ')) {
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      const headerText = line.substring(4);
      processedLines.push(`<h5 style="color: #666; margin: 15px 0 10px 0; font-size: 18px;">${headerText}</h5>`);
    } else if (line.startsWith('- ')) {
      // List item
      if (!inList) {
        processedLines.push('<ul style="margin: 15px 0; padding-left: 25px; list-style-type: disc;">');
        inList = true;
      }
      const listText = line.substring(2);
      processedLines.push(`<li style="margin: 8px 0; line-height: 1.4;">${listText}</li>`);
    } else if (line.startsWith('1. ')) {
      // Numbered list item
      if (!inList) {
        processedLines.push('<ol style="margin: 15px 0; padding-left: 25px;">');
        inList = true;
      }
      const listText = line.substring(3);
      processedLines.push(`<li style="margin: 8px 0; line-height: 1.4;">${listText}</li>`);
    } else {
      // Regular text - close any open list first
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      
      // Process bold and italic in the text
      let processedText = line
        .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: bold; color: #333;">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em style="font-style: italic; color: #555;">$1</em>');
      
      processedLines.push(`<p style="margin: 12px 0; line-height: 1.6; color: #333;">${processedText}</p>`);
    }
  }
  
  // Close any open list
  if (inList) {
    processedLines.push('</ul>');
  }
  
  const html = processedLines.join('');
  
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const MissionForm = ({ onSubmit, mission = null, style, showPreview, setShowPreview, onReset, subscriptionLessons }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [resources, setResources] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [subject, setSubject] = useState('');
  const [availableSubjects, setAvailableSubjects] = useState(['Black History']);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [_uploading, _setUploading] = useState(false);
  
  // Lesson selector state
  const [showLessonSelector, setShowLessonSelector] = useState(false);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  
  // Fetch available subjects (same as curriculum matcher and quiz creation)
  const fetchSubjects = async () => {
    try {
      const response = await axios.get(createApiUrl('/teacher/subjects'), {
        withCredentials: true
      });
      if (response.data.success) {
        setAvailableSubjects(response.data.data || ['Black History']);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Keep default subjects on error
      setAvailableSubjects(['Black History']);
    }
  };
  
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Expose resetForm to parent via ref if onReset ref provided
  useEffect(() => {
    if (onReset && typeof onReset === 'object' && 'current' in onReset) {
      onReset.current = resetForm;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resetForm identity intentionally excluded
  }, [onReset]);

  // Fetch lessons for selector
  const handleGetLessons = async () => {
    if (Array.isArray(subscriptionLessons)) {
      setAvailableLessons(subscriptionLessons);
      if (subscriptionLessons.length === 0) {
        alert('No lessons found. Please check your subscriptions.');
        return;
      }
      setShowLessonSelector(true);
      return;
    }

    setLoadingLessons(true);
    try {
      const response = await axios.get(createApiUrl('/lessons'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      const lessons = (Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.lessons) ? response.data.lessons : [])
      ).filter(isSubscriptionLessonSelectable);
      setAvailableLessons(lessons);
      if (lessons.length === 0) {
        alert('No lessons found. Please check your subscriptions.');
      } else {
        setShowLessonSelector(true);
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
      alert('Error fetching lessons. Please try again.');
    } finally {
      setLoadingLessons(false);
    }
  };

  // Handle lesson selection
  const handleSelectLesson = (lesson) => {
    const lessonDescription = lesson.description || '';
    setDescription(lessonDescription);
    setShowLessonSelector(false);
  };

  // Close lesson selector
  const handleCloseLessonSelector = () => {
    setShowLessonSelector(false);
  };

  // Prefill logic for new missions from AI
  useEffect(() => {
    
    if (mission) {
      // Editing existing mission
      setTitle(mission.title || '');
      setDescription(mission.description || '');
      setDueDate(mission.dueDate ? new Date(mission.dueDate).toISOString().split('T')[0] : '');
      setResources(mission.resources || '');
      // Convert backend values (beginner, intermediate, advanced) to display values
      const difficultyMap = {
        'beginner': 'beginner',
        'intermediate': 'intermediate',
        'advanced': 'advanced',
        'Easy': 'beginner', // Legacy support
        'Medium': 'intermediate', // Legacy support
        'Hard': 'advanced' // Legacy support
      };
      setDifficulty(difficultyMap[mission.difficulty] || '');
      setSubject(mission.subject || '');
      // Handle gradeLevel - use individual grade directly
      if (mission.gradeLevel) {
        setGradeLevel(mission.gradeLevel);
      }
    } else {
      // Check for prefill from AI
      const prefill = localStorage.getItem('prefillMission');
      
      if (prefill) {
        try {
          const data = JSON.parse(prefill);     
          
          setTitle(data.title || '');
          
          // For description, combine the key sections if they exist
          let fullDescription = '';
          if (data.learningObjectives) {
            fullDescription += `## Learning Objectives\n${data.learningObjectives}\n\n`;
          }
          if (data.contribution) {
            fullDescription += `## Contribution & Impact\n${data.contribution}\n\n`;
          }
          if (data.connection) {
            fullDescription += `## Connection to Topic\n${data.connection}\n\n`;
          }
          if (data.blackHistoryConnections) {
            fullDescription += `## Black History Connections\n${data.blackHistoryConnections}\n\n`;
          }
          if (data.instructions) {
            fullDescription += `## Instructions\n${data.instructions}\n\n`;
          }
          if (data.materials) {
            fullDescription += `## Materials Needed\n${data.materials}\n\n`;
          }
          if (data.assessment) {
            fullDescription += `## Assessment\n${data.assessment}\n\n`;
          }
          
          // Remove trailing newlines and set description
          setDescription(fullDescription.trim());
          
          // Set resources field
          setResources(data.resources || '');
          setDueDate(data.dueDate || '');
          // Convert backend values to display values
          const difficultyMap = {
            'beginner': 'beginner',
            'intermediate': 'intermediate',
            'advanced': 'advanced',
            'Easy': 'beginner', // Legacy support
            'Medium': 'intermediate', // Legacy support
            'Hard': 'advanced' // Legacy support
          };
          setDifficulty(difficultyMap[data.difficulty] || '');
          setGradeLevel(data.gradeLevel || '');
          setSubject(data.subject || '');
                   
          // Clear localStorage after successful prefill
          setTimeout(() => {
            localStorage.removeItem('prefillMission');
          }, 2000);
          
        } catch (error) {
          console.error('Error parsing prefill data:', error);
          setDescription(prefill);
        }
      } else {
      }
    }
  }, [mission]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check for matched historical content ID from curriculum matching
    const matchedHistoricalContentId = localStorage.getItem('matchedHistoricalContentId');
    
    // Map form fields to backend requirements
    const missionData = {
      title,
      description,
      missionType: 'time-travel',
      difficulty: difficulty || 'intermediate', // Default to intermediate if not selected
      gradeLevel: gradeLevel || null,
      subject: subject || null,
      objectives: [],
      tasks: [],
      puzzles: [],
      dueDate,
      resources,
      resourceTypes,
      uploadedFiles,
      // Include historical content ID if available from curriculum matching
      contentId: matchedHistoricalContentId || mission?.contentId || null
    };

    if (onSubmit) {
      onSubmit(missionData, mission?._id);
    } else {
      alert('Mission submitted!');
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setResources('');
    setDifficulty('');
    setGradeLevel('');
    setSubject('');
    setResourceTypes([]);
    setUploadedFiles([]);
    setShowPreview(false);
    setShowLessonSelector(false);
    setAvailableLessons([]);
  };

  // Expose resetForm to parent via useEffect if onReset callback provided
  useEffect(() => {
    if (onReset) {
      onReset.current = resetForm;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- resetForm identity intentionally excluded
  }, [onReset]);

  // File handling functions
  const handleFileUpload = (files) => {
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const handleFileRemove = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };


  const getFileIcon = (fileType) => {
    if (fileType.startsWith('video/')) return 'fa-video';
    if (fileType.startsWith('image/')) return 'fa-image';
    if (fileType.includes('pdf')) return 'fa-file-pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'fa-file-word';
    return 'fa-file';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="mission-form-container" style={style ? { ...style, background: '#fff', borderRadius: 8, padding: 16 } : { background: '#fff', borderRadius: 8, padding: 16, maxWidth: 900, margin: '0 auto' }}>


      {showPreview ? (
        <div className="preview-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div className="preview-modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '90%',
            maxHeight: '90%',
            width: '800px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}>
            <div className="preview-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 20px 15px 20px',
              borderBottom: '1px solid #eee',
              flexShrink: 0
            }}>
              <h4 style={{ margin: 0 }}>{title || 'Untitled Mission'}</h4>
              <button 
                type="button" 
                className="btn btn-outline-secondary"
                onClick={() => setShowPreview(false)}
                style={{
                  border: 'none',
                  fontSize: '24px',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            <div className="preview-body" style={{
              padding: '20px',
              flex: 1,
              overflow: 'auto'
            }}>
              <div className="mission-meta" style={{ marginBottom: '15px' }}>
                <span className="badge bg-primary me-2">Difficulty: {difficulty ? difficulty.charAt(0).toUpperCase() + difficulty.slice(1) : 'Not selected'}</span>
                {dueDate && <span className="badge bg-info me-2">Due: {dueDate}</span>}
              </div>
              
              <div className="description-preview" style={{
                border: '1px solid #eee',
                padding: '15px',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9',
                marginBottom: '15px'
              }}>
                <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
                  <strong>Debug:</strong> Raw description content: {description ? description.substring(0, 100) + '...' : 'None'}
                </div>
                <SimpleMarkdown>{description || 'No description provided'}</SimpleMarkdown>
              </div>
              
              {resources && (
                <div className="resources-preview" style={{
                  border: '1px solid #eee',
                  padding: '15px',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <h6>Resources:</h6>
                  <p style={{ margin: 0 }}>{resources}</p>
                </div>
              )}
            </div>
            
            <div className="preview-actions" style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              padding: '15px 20px',
              borderTop: '1px solid #eee',
              flexShrink: 0
            }}>
              <button 
                type="button" 
                className="btn btn-outline-secondary"
                onClick={() => setShowPreview(false)}
              >
                Back to Edit
              </button>
            </div>
          </div>
        </div>
      ) : (
        <form id="mission-form" className="mission-form" onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div className="mb-3">
            <label className="form-label">Title *</label>
            <input 
              className="form-control" 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              required 
              placeholder="Mission Title" 
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Description * (Supports Markdown)</label>
            <div style={{ marginBottom: '10px' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGetLessons}
                disabled={loadingLessons}
                style={{ 
                  fontSize: '0.9rem', 
                  padding: '0.5rem 1rem',
                  marginBottom: '10px',
                  width: '100%',
                  maxWidth: '200px'
                }}
              >
                {loadingLessons ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  '📚 Get Lessons'
                )}
              </button>
            </div>
            <textarea 
              className="form-control" 
              value={description} 
              onChange={e => setDescription(e.target.value)} 
              rows={8} 
              required 
              placeholder="Mission Description (you can use **bold**, *italic*, lists, etc.) Or click 'Get Lessons' above to select from available lessons." 
              style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }} 
            />
            <small className="text-muted">
              Markdown supported: **bold**, *italic*, - lists, # headers
            </small>
          </div>
          <div className="row mb-3">
            <div className="col-md-3">
              <label className="form-label">Subject</label>
              <select 
                className="form-select" 
                value={subject} 
                onChange={e => setSubject(e.target.value)}
              >
                <option value="">Select subject</option>
                {availableSubjects.map(subj => (
                  <option key={subj} value={subj}>{subj}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Difficulty</label>
              <select className="form-select" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                <option value="">Select level</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Grade Level</label>
              <select 
                className="form-select" 
                value={gradeLevel} 
                onChange={e => setGradeLevel(e.target.value)}
              >
                <option value="">Select grade</option>
                {GRADE_LEVELS.map(grade => (
                  <option key={grade.value} value={grade.value}>{grade.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Due Date</label>
              <input 
                className="form-control" 
                type="date" 
                value={dueDate} 
                onChange={e => setDueDate(e.target.value)} 
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Resources & Files</label>
            <textarea 
              className="form-control mb-2" 
              value={resources} 
              onChange={e => setResources(e.target.value)} 
              rows={2}
              placeholder="Add resource links, descriptions, or notes..."
            />
            

          </div>
          
          <div className="row mb-3">
            <div className="col-md-6 pe-md-3">
              <label className="form-label">Resource Types</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="resource-video"
                  checked={resourceTypes.includes('video')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setResourceTypes([...resourceTypes, 'video']);
                    } else {
                      setResourceTypes(resourceTypes.filter(type => type !== 'video'));
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="resource-video">
                  📹 Video Resources
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="resource-document"
                  checked={resourceTypes.includes('document')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setResourceTypes([...resourceTypes, 'document']);
                    } else {
                      setResourceTypes(resourceTypes.filter(type => type !== 'document'));
                    }
                  }}
                />
                <label className="form-label" htmlFor="resource-document">
                  📄 Document Resources
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="resource-interactive"
                  checked={resourceTypes.includes('interactive')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setResourceTypes([...resourceTypes, 'interactive']);
                    } else {
                      setResourceTypes(resourceTypes.filter(type => type !== 'interactive'));
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="resource-interactive">
                  🎮 Interactive Activities
                </label>
              </div>
            </div>
            
            <div className="col-md-6 ps-md-3">
              {/* File Upload Section */}
              <div className="file-upload-section">
                <label className="form-label">Upload Files</label>
                <DragDropFileUpload
                  onFileSelected={handleFileUpload}
                  accept="video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*,.mp4,.avi,.mov,.doc,.docx,.pdf,.jpg,.jpeg,.png"
                  multiple={true}
                  maxFileSize={50 * 1024 * 1024} // 50MB
                  supportedTypes={['.mp4', '.avi', '.mov', '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png']}
                  placeholder="Click to upload files or drag and drop"
                  description="Supports: Videos (MP4, AVI, MOV), Documents (PDF, DOC, DOCX), Images (JPG, PNG)"
                />
                
                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="uploaded-files mt-3">
                    <h6>Uploaded Files:</h6>
                    {uploadedFiles.map((file, index) => (
                      <div key={index} className="uploaded-file-item" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        marginBottom: '5px'
                      }}>
                        <div>
                          <i className={`fas ${getFileIcon(file.type)}`} style={{ marginRight: '8px' }}></i>
                          <span>{file.name}</span>
                          <small style={{ color: '#6c757d', marginLeft: '8px' }}>
                            ({formatFileSize(file.size)})
                          </small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleFileRemove(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

        </form>
      )}

      {/* Lesson Selector Modal */}
      {showLessonSelector && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={handleCloseLessonSelector}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '600px',
              maxHeight: '80vh',
              width: '90%',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Select a Lesson</h3>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseLessonSelector}
                aria-label="Close"
              ></button>
            </div>
            
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {loadingLessons ? (
                <div className="text-center py-4">
                  <p>Loading lessons...</p>
                </div>
              ) : availableLessons.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No lessons available. Please check your subscriptions.</p>
                </div>
              ) : (
                <div>
                  {availableLessons.map((lesson) => (
                    <div
                      key={lesson._id}
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        padding: '15px',
                        marginBottom: '10px',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={() => handleSelectLesson(lesson)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <h5 style={{ margin: '0 0 8px 0', color: '#333' }}>{lesson.name || 'Untitled Lesson'}</h5>
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        {lesson.description || 'No description available'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseLessonSelector}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionForm; 